import { useEffect, useRef, useState, useCallback } from "react";

import { getMapEditContext } from "@/api/objects";

const BBOX_DEBOUNCE_MS = 400;

const formatObjectsData = (data) => {
  const formatted = data.map(({ object, definition }) => {
    return definition.fields.map((field) => ({
      ...field,
      value: object[field.field_name],
    }));
  });

  return formatted.flat();
};

export const useObjectsLayer = (objectUuid, fieldName) => {
  const debounceTimeoutRef = useRef(null);

  const [allObjects, setAllObjects] = useState([]);
  const [allFields, setAllFields] = useState([]);
  const [bbox, setBbox] = useState(null);

  useEffect(() => {
    if (!bbox) return;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await getMapEditContext({ bbox: JSON.stringify(bbox) });
        const objects = res.objects ?? res.data ?? [];

        setAllObjects(
          formatObjectsData(
            objects.filter(({ object }) => object?.uuid !== objectUuid),
          ),
        );
        const currentObject = objects.find(
          ({ object }) => object?.uuid === objectUuid,
        );
        setAllFields(
          currentObject?.definition.fields
            .filter((field) => field.field_name !== fieldName)
            .map((field) => ({
              ...field,
              value: currentObject?.object[field.field_name],
            })) || [],
        );
      } catch (error) {
        console.error("Ошибка при загрузке данных объектов:", error);
      }
    }, BBOX_DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [objectUuid, fieldName, bbox]);

  const updateBbox = useCallback((map) => {
    if (!map) return;

    let bounds;
    try {
      bounds = map.getBounds();
    } catch (_) {
      return;
    }

    if (!bounds) return;

    const minLon = bounds.getWest();
    const minLat = bounds.getSouth();
    const maxLon = bounds.getEast();
    const maxLat = bounds.getNorth();

    setBbox((prev) => {
      if (
        prev?.minLat === minLat &&
        prev?.minLon === minLon &&
        prev?.maxLat === maxLat &&
        prev?.maxLon === maxLon
      ) {
        return prev;
      }

      return { minLat, minLon, maxLat, maxLon };
    });
  }, []);

  return {
    allObjects,
    allFields,
    updateBbox,
    setBbox,
  };
};
